import React, { ChangeEvent, FunctionComponent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, ButtonProps } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";

import { useAmplitude, useNotify } from "hooks";
import { UserRole, useUserRole } from "recoils";
import { EsgFolderUpload } from "assets";

const EXCEL_FILE_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const RecUploadButton: FunctionComponent<{
  label: string;
  uploadApiCall: ({
    file,
  }: {
    file: FormData;
  }) => Promise<AxiosResponse>;
  fileType?: string;
  accept?: string;
  queryKeyToInvalidate: string;
  availableToRoles: UserRole[];
  extraReqParams?: [string, string][];
  variant?: ButtonProps["variant"];
  marginTop?: string;
}> = ({
  uploadApiCall,
  fileType = EXCEL_FILE_TYPE,
  accept = `${EXCEL_FILE_TYPE}, application/vnd.ms-excel`,
  queryKeyToInvalidate,
  availableToRoles,
  label,
  extraReqParams,
  variant = "contained",
  marginTop = "10px",
}) => {
    const notify = useNotify();
    const userRole = useUserRole();
    const queryClient = useQueryClient();
    const { trackEvent } = useAmplitude();

    const { mutateAsync: upload, isLoading } = useMutation(uploadApiCall);

    const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
      const siteTab = window.sessionStorage.getItem("siteTab");
      if (siteTab === "0") { // 0 is the index of the Active Tab at Site Management Page
        trackEvent("Uploaded Site Data (Bulk)", "Site Management - Active");
      }
      const { files } = e.target;
      const formData = new FormData();

      if (files) {
        formData.append("file", new Blob([files[0]], { type: fileType }), files[0].name);

        if (extraReqParams) {
          extraReqParams.forEach(([key, value]) => {
            formData.append(key, value);
          });
        }

        try {
          notify.success('Your file upload was successful. Expect a notification in the top right bell once the data is available in ESG Flo.');
          const response = await upload({ file: formData })

          if (response?.data?.message) {
            setTimeout(() => {
              toast.dismiss();
            }, 3000);
          }
          queryClient.invalidateQueries([queryKeyToInvalidate]);
        } catch (error) {
          notify.error(
            `Failed to upload file: ${files[0].name}! (${(error as AxiosError)?.response?.data || "Internal Server Error"
            })`,
          );

          setTimeout(() => {
            toast.dismiss();
          }, 3000);
        } finally {
          e.target.value = "";
        }
      }
    };

    return (
      <Button
        sx={queryKeyToInvalidate === "sites" ? { marginTop, minWidth: "200px", fontWeight: "600", borderRadius: 2, height: "36px", } : { marginTop }}
        variant={variant}
        component="label"
        startIcon={<EsgFolderUpload />}
        disabled={isLoading || !userRole.isRoleIn(availableToRoles)}
      >
        {label}
        <input hidden accept={accept} type="file" onChange={onChange} />
      </Button>

    );
  };

